import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiClient } from '../services/apiClient';
import type {
  IncidenceEneaReadDTO,
  IncidenceEneaCreateDTO,
  IncidenceEneaPercentageReadDTO,
  IncidenceEneaPercentageCreateDTO,
  IIncidenceEneaReadDTO,
  IIncidenceEneaPercentageCreateDTO,
  IIncidenceEneaCreateDTO,
} from '../generated/api-client';

export type {
  IncidenceEneaReadDTO,
  IncidenceEneaCreateDTO,
  IncidenceEneaPercentageReadDTO,
  IncidenceEneaPercentageCreateDTO,
} from '../generated/api-client';

export const useIncidenceEnea = () => {
  return useQuery<IncidenceEneaReadDTO[]>('incidenceEnea', () =>
    apiClient.list_items_incidence_enea__get()
  );
};

export const useIncidenceEneaById = (id: string) => {
  return useQuery<IncidenceEneaReadDTO>(
    ['incidenceEnea', id],
    () => apiClient.get_item_incidence_enea__item_id__get(id),
    {
      enabled: !!id,
    }
  );
};

export const useCreateIncidenceEnea = () => {
  const queryClient = useQueryClient();

  return useMutation<IIncidenceEneaReadDTO, Error, IIncidenceEneaCreateDTO>(
    data => apiClient.create_item_incidence_enea__post(data as IncidenceEneaCreateDTO),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('incidenceEnea');
      },
    }
  );
};

export const useUpdateIncidenceEnea = () => {
  const queryClient = useQueryClient();

  return useMutation<IncidenceEneaReadDTO, Error, { id: string; data: IncidenceEneaCreateDTO }>(
    ({ id, data }) => apiClient.update_item_incidence_enea__item_id__put(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('incidenceEnea');
      },
    }
  );
};

export const useDeleteIncidenceEnea = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, string>(
    id => apiClient.delete_item_incidence_enea__item_id__delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('incidenceEnea');
      },
    }
  );
};

export const useIncidenceEneaPercentages = (incidenceEneaId?: string) => {
  return useQuery<IncidenceEneaPercentageReadDTO[]>(
    incidenceEneaId ? `incidenceEneaPercentages-${incidenceEneaId}` : 'incidenceEneaPercentages',
    () =>
      incidenceEneaId
        ? apiClient.get_by_incidence_enea_id_incidence_enea_percentage_by_incidence_enea__incidence_enea_id__get(
            incidenceEneaId
          )
        : Promise.resolve([]),
    {
      enabled: !!incidenceEneaId,
    }
  );
};

export const useCreateIncidenceEneaPercentage = () => {
  const queryClient = useQueryClient();

  return useMutation<IIncidenceEneaPercentageCreateDTO, Error, IncidenceEneaPercentageCreateDTO>(
    data => apiClient.create_item_incidence_enea_percentage__post(data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(`incidenceEnea`);
      },
    }
  );
};

export const useUpdateIncidenceEneaPercentage = () => {
  const queryClient = useQueryClient();

  return useMutation<
    IncidenceEneaPercentageReadDTO,
    Error,
    { id: string; data: IncidenceEneaPercentageCreateDTO }
  >(({ id, data }) => apiClient.update_item_incidence_enea_percentage__item_id__put(id, data), {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(`incidenceEnea`);
    },
  });
};

export const useDeleteIncidenceEneaPercentage = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, { id: string; incidenceEneaId: string }>(
    ({ id }) => apiClient.delete_item_incidence_enea_percentage__item_id__delete(id),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(`incidenceEnea`);
      },
    }
  );
};
